import axios from 'axios';
import { apiBaseUrl } from './apiBase';

export interface Cyclone {
    id: string;
    name: string;
    nameAr: string;
    category: number;
    maxWind: number;
    pressure: number;
    lat: number;
    lon: number;
    movement: { dir: number; speed: number };
    /** wind بالكيلومتر/ساعة لكل نقطة — يلزم لتعليق النقطة (كما في Zoom Earth). */
    track: { lat: number; lon: number; time: string; cat: number; wind: number }[];
    /** المسار المنقضي فعلاً (best track)، وتوقيتُه متى وفّره المصدر. */
    history: { lat: number; lon: number; time?: string }[];
    /** مضلّع المخروط الرسمي. فارغ ⇒ ترسم الواجهة مخروطها التقديري بدلاً منه. */
    /**
     * مضلّع المخروط الرسمي — **قائمةُ حلقات** لا قائمةُ نقاط.
     *
     * حلقة واحدة لا تكفي: حين تعبر العاصفة خطّ التاريخ الدولي يقسم المصدر المخروط
     * إلى مضلّعين (أحدهما ينتهي عند ‎180 والآخر يبدأ من ‎-180)، وتسطيحهما في قائمة
     * واحدة يَلحم طرفاً بطرفٍ لا يجاوره فيُرسم شريط عرضه العالم. حدث فعلاً مع
     * إعصار لالا. فارغة ⇒ ترسم الواجهة مخروطها التقديري بدلاً منه.
     */
    cone: { lat: number; lon: number }[][];
    basin: 'atlantic' | 'pacific' | 'indian';
}

interface CycloneApiItem {
    id?: string;
    name?: string;
    basin?: Cyclone['basin'];
    category?: number;
    max_wind_speed?: number;
    min_pressure?: number;
    latitude?: number;
    longitude?: number;
    movement?: { direction?: number; speed?: number };
    forecast_track?: Array<{ lat?: number; lon?: number; time?: string; wind?: number; category?: number }>;
    /** المسار المنقضي (best track من NHC) — نقاط مؤرخة حين يوفّرها ملف KML. */
    history_track?: Array<{ lat?: number; lon?: number; time?: string }>;
    /** مضلّع مخروط عدم اليقين الرسمي من NHC. */
    /** حلقات مضلّع المخروط. الصيغة القديمة (قائمة نقاط مسطّحة) ما تزال مقبولة. */
    track_cone?: Array<{ lat?: number; lon?: number }> | Array<Array<{ lat?: number; lon?: number }>>;
}

/** ينقّي مصفوفة نقاط جغرافية من الاستجابة (يُسقط أي قيمة غير رقمية). */
function toLatLngs(raw: Array<{ lat?: number; lon?: number; time?: string }> | undefined): Array<{ lat: number; lon: number; time?: string }> {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((p) => ({ lat: Number(p.lat), lon: Number(p.lon), time: typeof p.time === 'string' ? p.time : undefined }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
}

/**
 * يوحّد صيغتَي المخروط في قائمة حلقات.
 *
 * الخادم صار يُرجع حلقات (لازمٌ عند خطّ التاريخ حيث ينقسم المخروط مضلّعين)، لكن
 * البيانات المخزَّنة قبل التحديث ما تزال قائمة نقاط مسطّحة. نقبل الاثنتين كي لا
 * يختفي المخروط في الفترة بين نشر الواجهة ونشر الخادم.
 */
function toConeRings(
    raw: CycloneApiItem['track_cone'],
): { lat: number; lon: number }[][] {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const isNested = Array.isArray(raw[0]);
    const rings = isNested
        ? (raw as Array<Array<{ lat?: number; lon?: number }>>).map(toLatLngs)
        : [toLatLngs(raw as Array<{ lat?: number; lon?: number }>)];
    return rings.filter((ring) => ring.length >= 4);
}

function toCyclone(item: CycloneApiItem): Cyclone | null {
    if (!item.id || !Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)) return null;

    const maxWind = Number(item.max_wind_speed ?? 0);
    /* الباك اند يُرجع 0 لكل ما دون الفئة 1، فلا يفرّق بين عاصفة مدارية ومنخفض.
       نُعيد الاشتقاق من الرياح في هذا النطاق وحده (سلّم واحد لكل المصادر)، ونُبقي
       فئة NHC كما هي متى كانت 1 فأعلى — فهي تصنيفها الرسمي لا تقديرنا. */
    const rawCategory = Number(item.category ?? 0);
    const category = rawCategory >= 1 ? rawCategory : categoryFromWind(maxWind);
    const track = Array.isArray(item.forecast_track) ? item.forecast_track : [];

    return {
        id: item.id,
        name: item.name ?? item.id,
        nameAr: item.name ?? item.id,
        category,
        maxWind,
        pressure: Number(item.min_pressure ?? 0),
        lat: Number(item.latitude),
        lon: Number(item.longitude),
        movement: {
            dir: Number(item.movement?.direction ?? 0),
            speed: Number(item.movement?.speed ?? 0),
        },
        basin: item.basin ?? 'atlantic',
        history: toLatLngs(item.history_track),
        cone: toConeRings(item.track_cone),
        track: [
            {
                lat: Number(item.latitude), lon: Number(item.longitude), time: 'now',
                cat: category, wind: maxWind,
            },
            // نقاط الماضي المؤرخة تدخل المسار الزمني أيضاً؛ لا نضيف غير المؤرخة
            // كي لا تختلط بالمستقبل أو تمنع الاستيفاء.
            ...toLatLngs(item.history_track)
                .filter((point) => !!point.time && Number.isFinite(Date.parse(point.time)))
                .map((point) => ({ lat: point.lat, lon: point.lon, time: point.time!, cat: category, wind: maxWind })),
            ...track.map((point) => ({
                lat: Number(point.lat ?? item.latitude),
                lon: Number(point.lon ?? item.longitude),
                time: point.time ?? '',
                // نفس القاعدة لكل نقطة على حدة: فئتها الرسمية إن كانت إعصاراً،
                // وإلّا اشتقاق من رياحها هي — لا من فئة العاصفة الحالية.
                cat: Number(point.category ?? 0) >= 1
                    ? Number(point.category)
                    : categoryFromWind(Number(point.wind ?? 0)),
                wind: Number(point.wind ?? 0),
            })),
        ],
    };
}

/* ── صمود الجلب ───────────────────────────────────────────────────────────────
   بيانات الأعاصير تأتي من مصدرين خارجيين (NHC عبر خادمنا، وGDACS من المتصفّح
   مباشرةً)، وكلاهما يتعثّر عابراً: مهلة تنتهي، ‎5xx لثوانٍ، شبكةُ جوّالٍ تتذبذب.
   كانت أي عثرةٍ من هذه تُفرغ الطبقة حتى الدورة التالية — أي **خمس دقائق بلا
   إعصار على الخريطة** ثم عودته. وهو ما يراه المستخدم «اختفاءً مفاجئاً».

   العلاج طبقتان، وكلتاهما لا تمسّ العرض بحرف:
   1. محاولة ثانية بعد تأخّر قصير — تبتلع أغلب العثرات الوحيدة قبل أن تُرى.
   2. آخر نتيجة ناجحة لكل مصدر على حدة: إن سقط مصدرٌ بعد المحاولتين استُخدمت
      نتيجته الأخيرة ما دامت في نافذة صلاحية. المصدران مستقلّان، فسقوط GDACS
      لا يُسقط عواصف NHC ولا العكس.

   القائمة الفارغة العائدة من جلبٍ **ناجح** تُحفظ كما هي: تلاشي العاصفة غيابٌ
   صحيح يجب أن يظهر، ولا تُقاوَم إلّا حالة الفشل. */

/** نافذة صلاحية آخر نتيجة ناجحة. */
const LAST_GOOD_TTL_MS = 30 * 60 * 1000;

interface LastGood { at: number; value: Cyclone[] }

let lastGoodBackend: LastGood | null = null;
let lastGoodGdacs: LastGood | null = null;

/** آخر نتيجة ناجحة إن كانت ما تزال في النافذة، وإلّا null. */
function freshLastGood(entry: LastGood | null): Cyclone[] | null {
    if (!entry) return null;
    return Date.now() - entry.at < LAST_GOOD_TTL_MS ? entry.value : null;
}

/**
 * محاولةٌ واحدة إضافية عند الفشل.
 *
 * التأخّر قبلها مقصود: إعادةٌ فورية على شبكةٍ متذبذبة أو خادمٍ يتعافى تفشل غالباً
 * للسبب نفسه، فتضيف كلفةً بلا فائدة.
 */
async function withRetry<T>(task: () => Promise<T>, delayMs = 1500): Promise<T> {
    try {
        return await task();
    } catch {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return task();
    }
}

async function getBackendCyclones(): Promise<Cyclone[]> {
    // مهلة أطول لتحمّل الإقلاع البارد لخادم Laravel في التطوير.
    const response = await axios.get(`${apiBaseUrl}/cyclones/active`, { timeout: 15000 });
    const cyclones = Array.isArray(response.data?.cyclones) ? response.data.cyclones : [];
    return cyclones
        .map((item: CycloneApiItem) => toCyclone(item))
        .filter((item: Cyclone | null): item is Cyclone => item !== null);
}

/** بيانات NHC المخزّنة محلياً: مرحلة العرض الأولى للأنظمة النشطة. */
export async function getInitialActiveCyclones(): Promise<Cyclone[]> {
    return getBackendCyclones();
}

// ─── GDACS (تغطية عالمية) ─────────────────────────────────────────────────────
// خلفية NHC تغطي الأطلسي وشرق/وسط الهادئ فقط — أعاصير غرب الهادئ/المحيط الهندي
// (كإعصار الفلبين الظاهر على Zoom Earth) لا تصلها أبداً. GDACS (الاتحاد الأوروبي/الأمم
// المتحدة) يوفّر قائمة أعاصير عالمية بصيغة GeoJSON مع CORS مفتوح وبلا مفتاح.
/**
 * نقطة GDACS للأحداث الجارية.
 *
 * كانت `geteventlist/MAP?eventtypes=TC` وصارت تُرجع **400 لكل طلب** — فُحصت الصيغ
 * الثلاث (‏`eventtypes=TC`، `eventlist=TC`، وبلا معاملات) وكلّها 400، أي أن المسار
 * `MAP` نفسه أُزيل من الخدمة لا أن المعامل تغيّر. أثر ذلك أن تغطية الأعاصير العالمية
 * (غرب الهادئ والمحيط الهندي — وهي ما لا يصله NHC أصلاً) كانت **معطّلة بالكامل** بينما
 * يظهر الخطأ في الكونسول كضجيج خارجي لا كعطل ميزة.
 *
 * البديل المُختبَر: `EVENTS4APP`. يُرجع 200 مع `Access-Control-Allow-Origin: *`،
 * ويحمل الأحداث **الجارية** فعلاً (‏iscurrent=true) بخلاف `SEARCH` الذي يُرجع أرشيف
 * الأشهر الماضية بلا حدث جارٍ واحد.
 *
 * تحذير مقيس: `EVENTS4APP` **يتجاهل معامل `eventlist`** ويُرجع كل الأنواع مختلطة
 * (‏EQ زلازل، FL فيضانات، TC أعاصير، WF حرائق). فلا بدّ من ترشيح `eventtype === 'TC'`
 * في الكود؛ الاعتماد على المعامل وحده يُدخل زلازل وفيضانات إلى متتبّع الأعاصير.
 */
const GDACS_TC_URL = 'https://www.gdacs.org/gdacsapi/api/events/geteventlist/EVENTS4APP?eventlist=TC';

/**
 * موجز تفصيلي لحدث GDACS واحد — المسار الكامل ومخروطه والشدّة اللحظية.
 *
 * يمرّ عبر خادمنا لا عن المصدر مباشرةً: نطاق ‎/contentdata/ في GDACS لا يرسل
 * ترويسات CORS، فالطلب المباشر من المتصفّح يفشل قبل أن يصل الردّ (جُرِّب فعلاً).
 * الخادم يجلبه ويُقلّصه أيضاً — المصدر ‎~600KB أغلبها مضلّعات نطاقات رياح لا نرسمها.
 *
 * لماذا طلب إضافي لكل عاصفة: موجز القائمة (EVENTS4APP) يحمل حقلاً واحداً للشدّة هو
 * `severitydata.severity`، ونصّه يقول صراحةً "maximum wind speed" — أي **ذروة الحدث
 * لا شدّته الآن**. فكانت SAUDEL تُعرَض 231 كم/س (ذروتها المتوقّعة يوم 23 أغسطس)
 * بينما شدّتها الفعلية 130، وتُصنَّف فئة 4 وهي إعصار من الفئة 1.
 *
 * الموجز التفصيلي يحلّ ذلك وأكثر: `current_windspeed` للشدّة الآن، و21 نقطة مسار
 * (ماضٍ وتوقّع) بشدّة كل نقطة، ومضلّع مخروط رسمي — وكلّها تطابق ما يعرضه
 * Zoom Earth لنفس العاصفة نقطةً بنقطة.
 */
const gdacsDetailUrl = (eventId: number, episodeId: number): string =>
    `${apiBaseUrl}/cyclones/gdacs-track?event_id=${eventId}&episode_id=${episodeId}`;

/**
 * يقرأ المسار والمخروط والشدّة اللحظية من الموجز التفصيلي.
 * يُرجع null عند أي تعذّر، فيبقى ما استُخلص من القائمة كما هو بدل أن تختفي العاصفة.
 */
async function getGdacsDetail(eventId: number, episodeId: number): Promise<{
    wind: number;
    track: Cyclone['track'];
    cone: Cyclone['cone'];
} | null> {
    try {
        const { data, status } = await axios.get<{
            wind?: number;
            track?: Array<{ lat?: number; lon?: number; time?: string; wind?: number }>;
            cone?: Array<Array<{ lat?: number; lon?: number }>>;
            // 30 ثانية لا 20: أوّل طلب بعد انتهاء كاش الخادم يجلب ‎~600KB من GDACS
            // لكل عاصفة، وقيس فعلياً أنه يتجاوز 20 ثانية عند البرودة فتسقط كل
            // العواصف دفعةً واحدة إلى بيانات القائمة (أي إلى رقم الذروة الخاطئ).
        }>(gdacsDetailUrl(eventId, episodeId), { timeout: 30000 });

        // 204: الحدث قائم وموجزه التفصيلي غير منشور بعد — ليست حالة خطأ.
        if (status === 204 || !data) return null;

        const wind = Number(data.wind ?? 0);
        const track = (Array.isArray(data.track) ? data.track : [])
            .map((pt) => {
                const w = Math.round(Number(pt.wind ?? 0));
                return {
                    lat: Number(pt.lat),
                    lon: Number(pt.lon),
                    time: String(pt.time ?? ''),
                    cat: categoryFromWind(w),
                    wind: w,
                };
            })
            .filter((pt) => Number.isFinite(pt.lat) && Number.isFinite(pt.lon) && pt.time !== '');

        if (!Number.isFinite(wind) || track.length === 0) return null;
        const cone = Array.isArray(data.cone)
            ? data.cone.map((ring) => toLatLngs(ring)).filter((ring) => ring.length >= 4)
            : [];
        return { wind: Math.round(wind), track, cone };
    } catch {
        return null;
    }
}

interface GdacsFeature {
    geometry?: { type?: string; coordinates?: [number, number] };
    properties?: {
        eventid?: number;
        episodeid?: number;
        eventname?: string;
        eventtype?: string;
        todate?: string;
        iscurrent?: string | boolean;
        severitydata?: { severity?: number; severityunit?: string };
    };
}

/**
 * درجة الشدّة من سرعة الرياح (كم/س) على سلّم سافير-سيمبسون.
 *
 * الاصطلاح: 1..5 فئات الإعصار، 0 عاصفة مدارية، **‎-1 منخفض مداري**.
 * الدرجة السالبة أُضيفت لأن الدالة كانت تُرجع 0 لكل ما دون الفئة 1، فيظهر منخفض
 * بسرعة 55 كم/س «عاصفة مدارية» بينما تسمّيه NHC وZoom Earth منخفضاً (D). العتبة
 * 63 كم/س = 34 عقدة، وهي الحدّ الرسمي بين المنخفض والعاصفة لا رقماً مختاراً.
 */
export function categoryFromWind(kmh: number): number {
    if (kmh >= 252) return 5;
    if (kmh >= 209) return 4;
    if (kmh >= 178) return 3;
    if (kmh >= 154) return 2;
    if (kmh >= 119) return 1;
    if (kmh >= 63) return 0;
    return -1;
}

async function getGdacsCyclones(): Promise<Cyclone[]> {
    const response = await axios.get(GDACS_TC_URL, { timeout: 15000 });
    const features: GdacsFeature[] = Array.isArray(response.data?.features) ? response.data.features : [];

    // قد تَرِد عدّة معالم لكل حدث (نقاط مسار + مضلّعات) بنفس eventid — أول معلم نقطي
    // هو الموضع الرئيسي. نستبعد الأحداث المنتهية (todate أقدم من 24 ساعة) لأن iscurrent
    // يبقى true أحياناً بعد التلاشي.
    const byEvent = new Map<number, GdacsFeature[]>();
    for (const f of features) {
        const id = f.properties?.eventid;
        if (!id || f.geometry?.type !== 'Point') continue;
        // ترشيح النوع إلزامي: EVENTS4APP يخلط الزلازل والفيضانات والحرائق مع الأعاصير
        // ولا يحترم معامل eventlist (راجع GDACS_TC_URL).
        if (f.properties?.eventtype !== 'TC') continue;
        const arr = byEvent.get(id);
        if (arr) arr.push(f); else byEvent.set(id, [f]);
    }

    const cutoff = Date.now() - 24 * 3600 * 1000;

    // الأحداث الحيّة أوّلاً، ثم تفاصيل كلٍّ منها بالتوازي: العواصف النشطة قليلة
    // (وحدات لا عشرات) فالطلبات المتوازية أسرع بكثير من تسلسلها، وفشل أحدها
    // لا يُسقط البقيّة (allSettled).
    const live: { id: number; episodeId: number; lon: number; lat: number; props: NonNullable<GdacsFeature['properties']> }[] = [];
    for (const [id, points] of byEvent) {
        const main = points[0];
        const p = main.properties!;
        const to = p.todate ? Date.parse(`${p.todate}Z`) : NaN;
        if (Number.isFinite(to) && to < cutoff) continue;
        const [lon, lat] = main.geometry?.coordinates ?? [NaN, NaN];
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        live.push({ id, episodeId: Number(p.episodeid ?? 1), lon, lat, props: p });
    }

    const details = await Promise.allSettled(
        live.map((e) => getGdacsDetail(e.id, e.episodeId)),
    );

    const result: Cyclone[] = [];
    live.forEach((e, i) => {
        const settled = details[i];
        const detail = settled.status === 'fulfilled' ? settled.value : null;

        /* الشدّة الحالية من الموجز التفصيلي متى توفّر. `severitydata.severity`
           احتياطٌ فقط — وهو ذروة الحدث لا شدّته الآن، فيُبالغ أحياناً بالضعف. */
        const wind = detail?.wind ?? Number(e.props.severitydata?.severity ?? 0);
        const category = categoryFromWind(wind);
        const name = (e.props.eventname ?? `TC-${e.id}`).replace(/-\d+$/, '');

        /* نقطة «الآن» تتصدّر المسار — عقدٌ تعتمد عليه الواجهة (`track[0]`). نقاط
           الموجز التفصيلي تشمل الماضي والتوقّع معاً، فنضعها بعدها كما هي. */
        const nowPoint = { lat: e.lat, lon: e.lon, time: 'now', cat: category, wind: Math.round(wind) };
        const track = detail && detail.track.length > 0
            ? [nowPoint, ...detail.track]
            : [nowPoint];

        result.push({
            id: `gdacs-${e.id}`,
            name,
            nameAr: name,
            category,
            maxWind: Math.round(wind),
            pressure: 0,
            lat: e.lat,
            lon: e.lon,
            movement: { dir: 0, speed: 0 },
            history: [],
            // مخروط GDACS الرسمي (Poly_Cones) متى توفّر؛ وإلّا يبقى التقدير المحلّي.
            cone: detail?.cone ?? [],
            basin: 'pacific',
            track,
        });
    });

    return result;
}

/**
 * يدمج المصدرين: الخلفية (NHC — مسار توقّع مفصّل حيث توفّر) + GDACS (تغطية عالمية).
 * فشل أيّهما لا يُسقط الآخر؛ التكرار يُحسم بقرب الموضع (نفس العاصفة من المصدرين).
 *
 * كل مصدر يمرّ بمحاولةٍ ثانية ثم بآخر نتيجة ناجحة له (راجع «صمود الجلب» أعلاه)،
 * فلا يختفي إعصار لأنّ طلباً واحداً تعثّر. وترمي الدالة **فقط** حين يسقط المصدران
 * معاً ولا نتيجة محفوظة لأيّهما — عندئذٍ يُبقي المستدعي ما هو معروض بدل مسحه.
 */
export async function getActiveCyclones(): Promise<Cyclone[]> {
    const [backend, gdacs] = await Promise.allSettled([
        withRetry(getBackendCyclones),
        withRetry(getGdacsCyclones),
    ]);

    let primary: Cyclone[] | null;
    if (backend.status === 'fulfilled') {
        primary = backend.value;
        lastGoodBackend = { at: Date.now(), value: primary };
    } else {
        primary = freshLastGood(lastGoodBackend);
    }

    let global: Cyclone[] | null;
    if (gdacs.status === 'fulfilled') {
        global = gdacs.value;
        lastGoodGdacs = { at: Date.now(), value: global };
    } else {
        global = freshLastGood(lastGoodGdacs);
    }

    if (primary === null && global === null) {
        throw new Error('Both cyclone sources are unavailable and no recent data is cached');
    }

    const nhc = primary ?? [];
    const merged = [...nhc];
    for (const g of global ?? []) {
        const dup = nhc.some((c) => Math.abs(c.lat - g.lat) < 3 && Math.abs(c.lon - g.lon) < 3);
        if (!dup) merged.push(g);
    }
    return merged;
}

/* ── الاضطرابات المدارية (Tropical Weather Outlook) ────────────────────────────
   ليست أعاصير بل مناطق يرصدها NHC لاحتمال تكوّنها نظاماً مدارياً. تُعرَض نقطةً
   داخل مساحة تطوّرها المحتمل — وهو ما يعرضه Zoom Earth بالنقاط البنفسجية. */
export interface Disturbance {
    id: string;
    basin: string;
    area: string;
    lat: number;
    lon: number;
    /** احتمال التكوّن خلال يومين / سبعة أيام (نسبة مئوية). */
    prob2day: number;
    prob7day: number;
    /** مستوى الخطورة كما يصنّفه NHC: Low / Medium / High. */
    risk2day: string;
    risk7day: string;
    /** مضلّع منطقة التطوّر المحتمل. فارغ ⇒ تُرسم النقطة وحدها. */
    area_polygon: { lat: number; lon: number }[];
}

interface DisturbanceApiItem {
    id?: string;
    basin?: string;
    area?: string;
    latitude?: number;
    longitude?: number;
    prob_2day?: number;
    prob_7day?: number;
    risk_2day?: string;
    risk_7day?: string;
    area_polygon?: Array<{ lat?: number; lon?: number }>;
}

/**
 * يُرجع `null` عند تعذّر الجلب — لا `[]`.
 *
 * التمييز هو الفرق بين «لا اضطرابات الآن» و«لم نستطع السؤال»: الأولى يجب أن تُفرّغ
 * الطبقة، والثانية يجب أن تُبقي آخر ما عُرض. كان التعذّر يُرجع `[]` فيُمحى كل شيء
 * لخمس دقائق عند أول عثرة شبكة. (والخادم صار كذلك لا يخزّن فشله — راجع
 * `CycloneTrackingService::getDisturbances`.)
 */
export async function getDisturbances(): Promise<Disturbance[] | null> {
    try {
        const { data } = await axios.get<{ disturbances?: DisturbanceApiItem[] }>(
            `${apiBaseUrl}/cyclones/disturbances`,
            { timeout: 15000 },
        );
        const items = Array.isArray(data?.disturbances) ? data.disturbances : [];
        return items
            .filter((d): d is DisturbanceApiItem =>
                !!d?.id && Number.isFinite(d.latitude) && Number.isFinite(d.longitude))
            .map((d) => ({
                id: d.id!,
                basin: d.basin ?? '',
                area: d.area ?? '',
                lat: Number(d.latitude),
                lon: Number(d.longitude),
                prob2day: Number(d.prob_2day ?? 0),
                prob7day: Number(d.prob_7day ?? 0),
                risk2day: d.risk_2day ?? '',
                risk7day: d.risk_7day ?? '',
                area_polygon: toLatLngs(d.area_polygon),
            }));
    } catch {
        // تعذّر الجلب — ليست حالة خطأ توقف الأعاصير نفسها، وليست «صفر اضطرابات».
        return null;
    }
}
